import { allUploadAccept, audioMimeTypes, imageMimeTypes, videoMimeTypes } from '@/components/FileUpload'
import type { ResourceType } from '@/services/api/api.types'
import { canSelectMediaMode, defaultDeliveryModeName, isMediaModeName } from '@/utils/deliveryModes'
import { PSK_VALUE } from '@/utils/sidekicks'

export type BulkUploadMode = 'modules' | 'supplementary'

/** S3's ceiling for one `PUT`, which is all V1's transport has — SME-1111 decision 51. */
export const MAX_BULK_FILE_BYTES = 5 * 1024 ** 3
export const MAX_BULK_FILE_LABEL = '5 GB'

export type StagedFile = {
  file: File
  type: ResourceType
  /** `file.type`, or the extension's when the browser reported none — `/prepare-upload` refuses an empty one. */
  mimeType: string
  title: string
  /** The PM's pick for this file. Null follows the Sidekick default above the table. */
  eskId: string | null
  /** The PM's pick for this file. Null follows the delivery mode default above the table. */
  deliveryModeName: string | null
}

/** What every file follows until the PM picks for it (decision 10). */
export type BulkDefaults = {
  eskId: string
  /** Null is By file type: each file gets its own type's default. */
  deliveryModeName: string | null
}

export const INITIAL_DEFAULTS: BulkDefaults = { eskId: PSK_VALUE, deliveryModeName: null }

/** A file as the job takes it, with its Sidekick resolved. */
export type ConfirmedFile = Omit<StagedFile, 'eskId'> & { eskId: string }

const mimeResourceTypes: Record<string, ResourceType> = {
  'text/plain': 'text',
  'text/markdown': 'text',
  'text/x-markdown': 'text',
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.ms-excel.sheet.macroEnabled.12': 'xlsx',
  'text/csv': 'csv',
  'application/csv': 'csv',
  ...Object.fromEntries(videoMimeTypes.map(mime => [mime, 'video'])),
  ...Object.fromEntries(audioMimeTypes.map(mime => [mime, 'audio'])),
  ...Object.fromEntries(imageMimeTypes.map(mime => [mime, 'image'])),
}

const extensionMimes = new Map(
  Object.entries(allUploadAccept).flatMap(([mime, extensions]) => extensions.map(ext => [ext, mime] as const)),
)

export const fileExtension = (name: string): string => {
  const dot = name.lastIndexOf('.')
  return dot > 0 ? name.slice(dot).toLowerCase() : ''
}

export const fileTypeLabel = (name: string): string => fileExtension(name).slice(1).toUpperCase()

/**
 * What the backend will make of the file, read before it is uploaded. Falls back to the
 * extension because browsers often report `.md` with no MIME type, as the dropzone does.
 */
export function resourceTypeForFile(file: Pick<File, 'name' | 'type'>): ResourceType | undefined {
  const mime = mimeTypeForFile(file)
  return mime ? mimeResourceTypes[mime] : undefined
}

export function mimeTypeForFile(file: Pick<File, 'name' | 'type'>): string | undefined {
  if (file.type in allUploadAccept && mimeResourceTypes[file.type]) return file.type
  return extensionMimes.get(fileExtension(file.name))
}

/**
 * Her rule: extension removed, underscores to spaces, and an all-lower-case name title-cased.
 * Anything with a capital in it is taken as the PM's own casing and kept.
 */
export function titleFromFileName(name: string): string {
  const dot = name.lastIndexOf('.')
  const base = (dot > 0 ? name.slice(0, dot) : name).replace(/_/g, ' ').replace(/\s+/g, ' ').trim()
  if (base !== base.toLowerCase()) return base
  return base.replace(/(^|\s)(\S)/g, (_, space: string, letter: string) => space + letter.toUpperCase())
}

export const titleError = (title: string): string | undefined => (title.trim() ? undefined : 'Title is required.')

export const canStagedFileUseMedia = (staged: Pick<StagedFile, 'type'>): boolean =>
  canSelectMediaMode({ type: staged.type, showInAppSupported: undefined })

const mediaBarred = (staged: StagedFile, modeName: string) =>
  isMediaModeName(modeName) && !canStagedFileUseMedia(staged)

/** The default where the file can take it; Media on a file that can't, the same rule single upload has, gives its type's. */
export function defaultModeFor(staged: StagedFile, styleNames: string[], defaults: BulkDefaults): string | undefined {
  const picked = defaults.deliveryModeName
  if (picked && styleNames.includes(picked) && !mediaBarred(staged, picked)) return picked
  return defaultDeliveryModeName({ type: staged.type, showInAppSupported: undefined }, styleNames)
}

export function resolvedDeliveryMode(
  staged: StagedFile,
  styleNames: string[],
  defaults: BulkDefaults = INITIAL_DEFAULTS,
): string | undefined {
  return staged.deliveryModeName ?? defaultModeFor(staged, styleNames, defaults)
}

export const resolvedSidekick = (staged: StagedFile, defaults: BulkDefaults): string => staged.eskId ?? defaults.eskId

/** Following a Media default it can't take, so it has its type's default instead. */
export const skipsMediaDefault = (staged: StagedFile, defaults: BulkDefaults): boolean =>
  staged.deliveryModeName === null && !!defaults.deliveryModeName && mediaBarred(staged, defaults.deliveryModeName)

// A pick that matches what the default already gives the file follows the default, rather than
// customizing the file to the same value and cutting it off from later changes.
export const pickSidekick = (staged: StagedFile, eskId: string, defaults: BulkDefaults): StagedFile => ({
  ...staged,
  eskId: eskId === defaults.eskId ? null : eskId,
})

export const pickDeliveryMode = (
  staged: StagedFile,
  modeName: string,
  styleNames: string[],
  defaults: BulkDefaults,
): StagedFile => ({
  ...staged,
  deliveryModeName: modeName === defaultModeFor(staged, styleNames, defaults) ? null : modeName,
})

export const isCustomized = (staged: StagedFile): boolean => staged.eskId !== null || staged.deliveryModeName !== null

export const followDefaults = (files: StagedFile[]): StagedFile[] =>
  files.map(staged => ({ ...staged, eskId: null, deliveryModeName: null }))

export type DefaultField = keyof BulkDefaults

export type DefaultChange = {
  field: DefaultField
  /** Files whose value moved, to highlight. */
  changed: string[]
  /** Files now on the new default. */
  applied: number
  /** Customized files, left as the PM set them. */
  kept: number
  /** Files that can't take a Media default. */
  skipped: number
}

export function describeDefaultChange(
  files: StagedFile[],
  styleNames: string[],
  field: DefaultField,
  before: BulkDefaults,
  after: BulkDefaults,
): DefaultChange {
  const resolve = (staged: StagedFile, defaults: BulkDefaults) =>
    field === 'eskId' ? resolvedSidekick(staged, defaults) : resolvedDeliveryMode(staged, styleNames, defaults)
  const change: DefaultChange = { field, changed: [], applied: 0, kept: 0, skipped: 0 }
  for (const staged of files) {
    if (staged[field] !== null) {
      change.kept++
      continue
    }
    if (resolve(staged, before) !== resolve(staged, after)) change.changed.push(staged.file.name)
    if (field === 'deliveryModeName' && skipsMediaDefault(staged, after)) change.skipped++
    else change.applied++
  }
  return change
}

export type AddFilesResult = {
  files: StagedFile[]
  unsupported: string[]
  tooLarge: string[]
}

/** A file's name is its identity in the wizard, so a repeated name is dropped rather than staged twice. */
export function addFiles(existing: StagedFile[], incoming: File[]): AddFilesResult {
  const seen = new Set(existing.map(staged => staged.file.name))
  const files = [...existing]
  const unsupported: string[] = []
  const tooLarge: string[] = []

  for (const file of incoming) {
    if (seen.has(file.name)) continue
    seen.add(file.name)

    const mimeType = mimeTypeForFile(file)
    const type = mimeType ? mimeResourceTypes[mimeType] : undefined
    if (!type || !mimeType) {
      unsupported.push(file.name)
    } else if (file.size > MAX_BULK_FILE_BYTES) {
      tooLarge.push(file.name)
    } else {
      files.push({
        file,
        type,
        mimeType,
        title: titleFromFileName(file.name),
        eskId: null,
        deliveryModeName: null,
      })
    }
  }

  return { files, unsupported, tooLarge }
}

const typeGroups: Array<{ label: string; types: ResourceType[] }> = [
  { label: 'video', types: ['video'] },
  { label: 'audio', types: ['audio'] },
  { label: 'documents', types: ['pdf', 'docx', 'text'] },
  { label: 'presentations', types: ['pptx'] },
  { label: 'images', types: ['image'] },
  { label: 'spreadsheets', types: ['csv', 'xlsx'] },
]

/** Written from the accept list rather than beside it, so the copy cannot drift from what is taken. */
export const supportedTypeGroups: Array<{ label: string; extensions: string[] }> = typeGroups.map(
  ({ label, types }) => ({
    label,
    extensions: [
      ...new Set([...extensionMimes].filter(([, mime]) => types.includes(mimeResourceTypes[mime])).map(([ext]) => ext)),
    ],
  }),
)

export const supportedTypesText: string = supportedTypeGroups
  .map(({ label, extensions }) => `${label} (${extensions.join(', ')})`)
  .join(', ')

/** Everything the picker should offer. Extensions as well as MIME types, for the `.md` reason above. */
export const bulkUploadAcceptAttribute: string = [...Object.keys(allUploadAccept), ...extensionMimes.keys()].join(',')

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const units = ['KB', 'MB', 'GB']
  let value = bytes / 1024
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit++
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`
}

export const totalSize = (files: StagedFile[]): number => files.reduce((sum, staged) => sum + staged.file.size, 0)
